import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useBasePath } from '@/lib/paths'
import { FileText } from 'lucide-react'
import { DocumentUploader } from '@/components/shared/DocumentUploader'
import { TemplateList } from '@/components/shared/TemplateList'

type Document = {
  id: string
  name: string
  file_url?: string | null
  blob_signed_id?: string | null
  filename?: string | null
  created_at: string
}

type Template = { id: string; name: string; file_url: string | null }

type Props = {
  formation: { id: string; legal_name: string }
  templates: Template[]
  application_documents: Document[]
  filing_documents: Document[]
  completion_documents: Document[]
}

export default function DocumentsShow({ formation, templates, application_documents, filing_documents, completion_documents }: Props) {
  const basePath = useBasePath()

  return (
    <DashboardLayout
      title={`Documents — ${formation.legal_name}`}
      breadcrumbs={[
        { label: 'Formations', href: 'formations' },
        { label: formation.legal_name, href: `formations/${formation.id}` },
        { label: 'Documents' }
      ]}
    >
      <div>
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-4 h-4 text-muted-foreground" />
            <h1 className="text-xl font-semibold tracking-tight">Documents</h1>
          </div>
          <p className="text-sm text-muted-foreground">{formation.legal_name}</p>
        </div>

        <div className="space-y-6">
          {/* Document Templates */}
          <TemplateList title="Document Templates" templates={templates || []} />

          {/* Application Documents */}
          <DocumentUploader
            title="Application Documents"
            documents={application_documents || []}
            uploadPath={`${basePath}/formations/${formation.id}/application_documents`}
            deletePath={(docId) => `${basePath}/formations/${formation.id}/application_documents/${docId}`}
          />

          {/* Filing Documents */}
          {filing_documents.length > 0 && (
            <DocumentUploader
              title="Filing Documents"
              documents={filing_documents}
              uploadPath={`${basePath}/formations/${formation.id}/filing_documents`}
              deletePath={(docId) => `${basePath}/formations/${formation.id}/filing_documents/${docId}`}
            />
          )}

          {/* Completion Documents */}
          {completion_documents.length > 0 && (
            <DocumentUploader
              title="Completion Documents"
              documents={completion_documents}
              uploadPath={`${basePath}/formations/${formation.id}/completion_documents`}
              deletePath={(docId) => `${basePath}/formations/${formation.id}/completion_documents/${docId}`}
            />
          )}
        </div>
      </div>
    </DashboardLayout>
  )
}
